// Select - portal-rendered, viewport-aware custom dropdown (typed options).

import { Check, ChevronDown } from 'lucide-react';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/classnames';
import { overlayHost } from '@/lib/toplayer';

export type SelectOption<T extends string = string> = {
  value: T;
  label: string;
  description?: string;
  disabled?: boolean;
  // Leading slot (icon/swatch) shown left of the label; keep it 14–18px.
  leading?: ReactNode;
};

/**
 * Portal-rendered, position:fixed dropdown (Radix/Headless-style), so no
 * ancestor overflow/transform/z-index can clip or break it. Auto-flips up
 * when there's no room below and clamps to the viewport. Page scroll closes
 * it - cheaper and less janky than recomputing position continuously.
 */
export function Select<T extends string = string>({
  value,
  onChange,
  options,
  icon,
  className,
  ariaLabel,
  placeholder,
  disabled,
  size = 'md',
  autoFocus,
}: {
  value: T | '';
  onChange: (next: T) => void;
  options: ReadonlyArray<SelectOption<T>>;
  icon?: ReactNode;
  className?: string;
  ariaLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  /** Marks the trigger `data-autofocus`, which is how Modal focuses a field on
   *  open. React's own `autoFocus` is silently lost inside a `<dialog>`, and a
   *  dialog whose first field is a Select had no way to say so. */
  autoFocus?: boolean;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState<number>(-1);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; placement: 'below' | 'above' } | null>(null);
  const listboxId = useId();

  const selectedIdx = useMemo(
    () => options.findIndex((o) => o.value === value),
    [options, value],
  );
  const selectedOption = selectedIdx >= 0 ? options[selectedIdx] : undefined;
  const triggerLabel = selectedOption?.label ?? placeholder ?? 'Select…';

  // Compute the panel's fixed coordinates from the trigger rect.
  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const gap = 4;
    const margin = 8;
    // Estimated height for the flip decision (real height isn't known
    // until render); generous upper bound is good enough.
    const estimatedHeight = Math.min(320, Math.max(48, options.length * 38 + 8));
    const spaceBelow = vh - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const openBelow = spaceBelow >= estimatedHeight || spaceBelow >= spaceAbove;
    // Width: at least trigger width, capped so the panel never exits
    // the viewport on the right.
    const minWidth = Math.max(rect.width, 192);
    const maxAllowedWidth = vw - 2 * margin;
    const width = Math.min(minWidth, maxAllowedWidth);
    let left = rect.left;
    if (left + width > vw - margin) left = vw - margin - width;
    if (left < margin) left = margin;
    const top = openBelow ? rect.bottom + gap : rect.top - gap - estimatedHeight;
    setPos({ top, left, width, placement: openBelow ? 'below' : 'above' });
  }, [options.length]);

  const openMenu = useCallback(() => {
    if (disabled) return;
    setHighlight(selectedIdx >= 0 ? selectedIdx : firstEnabledIdx(options));
    setOpen(true);
  }, [disabled, selectedIdx, options]);

  const closeMenu = useCallback(
    (returnFocus = true) => {
      setOpen(false);
      if (returnFocus) {
        // Only if nothing took focus before the frame: the closing panel held
        // it, so anything but the body or the panel is the user moving on, and
        // taking focus back would swallow what they type there.
        requestAnimationFrame(() => {
          const active = document.activeElement;
          if (active === null || active === document.body || panelRef.current?.contains(active)) {
            triggerRef.current?.focus();
          }
        });
      }
    },
    [],
  );

  // Position the panel synchronously on open so there's no "popover
  // appears at (0,0) for one frame then jumps" flash.
  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition]);

  // Close on outside scroll / resize. The capture-phase scroll listener
  // catches any page scroll but must exclude scrolls inside the panel
  // (else every wheel tick over the option list would close the menu).
  useEffect(() => {
    if (!open) return;
    function onScroll(e: Event) {
      const t = e.target as Node | null;
      if (t && panelRef.current?.contains(t)) return; // inside-panel scroll, ignore
      closeMenu(false);
    }
    function onResize() {
      closeMenu(false);
    }
    function onPointer(e: PointerEvent) {
      const t = e.target as Node;
      if (
        !triggerRef.current?.contains(t) &&
        !panelRef.current?.contains(t)
      ) {
        closeMenu(false);
      }
    }
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    window.addEventListener('pointerdown', onPointer);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('pointerdown', onPointer);
    };
  }, [open, closeMenu]);

  function onTriggerKey(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openMenu();
    }
  }

  const typeBufferRef = useRef<{ buf: string; ts: number }>({ buf: '', ts: 0 });
  function onPanelKey(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeMenu();
      return;
    }
    if (e.key === 'Tab') {
      closeMenu(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => nextEnabledIdx(options, h, 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => nextEnabledIdx(options, h, -1));
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      setHighlight(firstEnabledIdx(options));
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      setHighlight(lastEnabledIdx(options));
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const opt = options[highlight];
      if (opt && !opt.disabled) {
        onChange(opt.value);
        closeMenu();
      }
      return;
    }
    if (e.key.length === 1 && /\S/.test(e.key)) {
      e.preventDefault();
      const now = performance.now();
      const buf = now - typeBufferRef.current.ts < 700 ? typeBufferRef.current.buf : '';
      const next = (buf + e.key).toLowerCase();
      typeBufferRef.current = { buf: next, ts: now };
      const match = options.findIndex(
        (o) => !o.disabled && o.label.toLowerCase().startsWith(next),
      );
      if (match >= 0) setHighlight(match);
    }
  }

  useEffect(() => {
    if (!open || highlight < 0) return;
    optionRefs.current[highlight]?.scrollIntoView({ block: 'nearest' });
  }, [open, highlight]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => panelRef.current?.focus());
    }
  }, [open]);

  return (
    <div className={cn('relative', className)}>
      <button
        type="button"
        ref={triggerRef}
        onClick={() => (open ? closeMenu(false) : openMenu())}
        onKeyDown={onTriggerKey}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={ariaLabel}
        disabled={disabled}
        data-autofocus={autoFocus ? '' : undefined}
        className={cn(
          // `relative` anchors the chevron/icon to the button (not the
          // wrapper); `w-full` lets the caller's wrapper className set width.
          'relative w-full',
          size === 'md' && 'input cursor-pointer text-left inline-flex items-center gap-2 pr-9',
          size === 'sm' &&
            // leading-tight, not leading-none: the label below is `truncate`,
            // whose overflow:hidden clips whatever the 1em line box cannot
            // hold. Inter's ascent+descent is ~1.21em, so at leading-none the
            // halves of "p" and "g" that fall outside the line box are cut —
            // a 12px box clipping a ~14.5px glyph, which is what "50 per
            // page" showed. 1.25em holds the descenders inside the box.
            'ring-focus cursor-pointer text-left inline-flex items-center gap-1.5 ' +
              'h-7 pr-6 rounded-md text-xs leading-tight ' +
              'bg-ink-900/60 border border-ink-700/70 ' +
              'text-ink-200 hover:text-ink-100 hover:bg-ink-900 ' +
              'focus:border-accent-500 focus:bg-ink-900 transition-colors',
          icon ? (size === 'sm' ? 'pl-6' : 'pl-9') : (size === 'sm' ? 'pl-2.5' : 'pl-3'),
          disabled && 'opacity-50 cursor-not-allowed',
          open && 'border-accent-500/80 bg-ink-900',
        )}
      >
        {icon && (
          <span
            aria-hidden="true"
            className={cn(
              'absolute top-1/2 -translate-y-1/2 text-ink-500 pointer-events-none flex items-center',
              size === 'sm' ? 'left-2' : 'left-3',
            )}
          >
            {icon}
          </span>
        )}
        {selectedOption?.leading && (
          <span aria-hidden="true" className="shrink-0 flex items-center">
            {selectedOption.leading}
          </span>
        )}
        <span
          className={cn(
            'flex-1 min-w-0 truncate',
            !selectedOption && placeholder !== undefined && 'text-ink-500',
          )}
          title={selectedOption?.label}
        >
          {triggerLabel}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={cn(
            'absolute top-1/2 -translate-y-1/2 text-ink-500 pointer-events-none transition-transform',
            size === 'sm' ? 'right-1.5 w-3 h-3' : 'right-3 w-4 h-4',
            open && 'rotate-180 text-ink-300',
          )}
        />
      </button>

      {open && pos &&
        createPortal(
          <div
            id={listboxId}
            ref={panelRef}
            role="listbox"
            tabIndex={-1}
            aria-label={ariaLabel}
            aria-activedescendant={
              highlight >= 0 ? `${listboxId}-opt-${highlight}` : undefined
            }
            onKeyDown={onPanelKey}
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              width: pos.width,
              zIndex: 100,
            }}
            className={cn(
              'card p-1',
              'max-h-[60vh] overflow-y-auto overscroll-y-auto',
              'focus:outline-none',
              pos.placement === 'below' ? 'animate-slide-down' : 'animate-slide-up',
            )}
          >
            {options.length === 0 && (
              <div className="px-3 py-2 text-xs text-ink-500">No options</div>
            )}
            {options.map((opt, idx) => {
              const isSelected = opt.value === value;
              const isHighlighted = idx === highlight;
              return (
                <button
                  key={opt.value}
                  id={`${listboxId}-opt-${idx}`}
                  ref={(el) => {
                    optionRefs.current[idx] = el;
                  }}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={opt.disabled || undefined}
                  onMouseEnter={() => !opt.disabled && setHighlight(idx)}
                  onClick={() => {
                    if (opt.disabled) return;
                    onChange(opt.value);
                    closeMenu();
                  }}
                  disabled={opt.disabled}
                  title={opt.label}
                  className={cn(
                    'w-full text-left rounded-md transition-colors',
                    // Row size scales with the trigger size so a sm Select
                    // doesn't open into oversized rows.
                    'flex items-start gap-2',
                    size === 'sm' ? 'px-2.5 py-1.5' : 'px-3 py-2',
                    isHighlighted && !opt.disabled && 'bg-ink-800/70',
                    isSelected && !isHighlighted && 'bg-ink-800/40',
                    opt.disabled
                      ? 'opacity-50 cursor-not-allowed text-ink-500'
                      : 'cursor-pointer text-ink-100',
                  )}
                >
                  {opt.leading && (
                    <span
                      aria-hidden="true"
                      className="shrink-0 flex items-center mt-0.5"
                    >
                      {opt.leading}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div
                      className={cn(
                        'truncate',
                        size === 'sm' ? 'text-xs leading-tight' : 'text-sm',
                      )}
                    >
                      {opt.label}
                    </div>
                    {opt.description && (
                      <div
                        className={cn(
                          'text-ink-500 truncate',
                          size === 'sm' ? 'text-[10px]' : 'text-[11px]',
                        )}
                      >
                        {opt.description}
                      </div>
                    )}
                  </div>
                  {isSelected && (
                    <Check
                      className={cn(
                        'text-accent-400 shrink-0',
                        size === 'sm' ? 'w-3 h-3 mt-0.5' : 'w-4 h-4 mt-0.5',
                      )}
                    />
                  )}
                </button>
              );
            })}
          </div>,
          // Not document.body. showModal() puts a dialog in the top layer,
          // where its backdrop paints over everything outside the dialog
          // whatever the z-index says, so a listbox portalled to <body> from
          // a Select inside a dialog rendered under the scrim and took no
          // clicks. Rendering into the dialog keeps the fixed positioning
          // correct: .modal sets no transform or filter, so it is not a
          // containing block for fixed children and its overflow:hidden
          // does not clip them either.
          overlayHost(triggerRef.current),
        )}
    </div>
  );
}

function firstEnabledIdx<T extends string>(options: ReadonlyArray<SelectOption<T>>): number {
  for (let i = 0; i < options.length; i++) if (!options[i].disabled) return i;
  return -1;
}
function lastEnabledIdx<T extends string>(options: ReadonlyArray<SelectOption<T>>): number {
  for (let i = options.length - 1; i >= 0; i--) if (!options[i].disabled) return i;
  return -1;
}
function nextEnabledIdx<T extends string>(
  options: ReadonlyArray<SelectOption<T>>,
  current: number,
  dir: 1 | -1,
): number {
  if (options.length === 0) return -1;
  let i = current;
  for (let n = 0; n < options.length; n++) {
    i = (i + dir + options.length) % options.length;
    if (!options[i].disabled) return i;
  }
  return current;
}
